import { z } from 'zod'

import { ProposalSystemErrorCodeEnum } from './proposal.ts'

export const LLMProviderEnum = z.enum(['bedrock', 'openrouter', 'gemini'])
export type LLMProvider = z.infer<typeof LLMProviderEnum>

export const NormalizedLLMErrorKindEnum = z.enum([
  'access_denied',
  'model_not_found',
  'model_unavailable',
  'service_unavailable',
  'model_throttled',
  'model_retired',
  'invalid_output',
  'request_failed'
])
export type NormalizedLLMErrorKind = z.infer<typeof NormalizedLLMErrorKindEnum>

// Why a provider request that reached the model produced nothing usable. Only meaningful
// alongside kind `invalid_output`; every other kind is a transport or availability failure.
// `truncated` is deliberately distinct from `no_matching_tool_call`: the model did start the
// structured response and ran out of output tokens, which is a maxTokens bug on our side, not a
// model that ignored the schema. Conflating them sends you hunting the wrong cause (RAL-191).
export const LLMOutputFailureEnum = z.enum(['no_matching_tool_call', 'truncated', 'json_parse', 'schema_validation'])
export type LLMOutputFailure = z.infer<typeof LLMOutputFailureEnum>

// Allowlisted Zod failure detail: issue code plus dotted field path only. The issue's `message`
// and `received`/`input` carry fragments of the raw model output, so they are dropped here
// rather than at each log site.
export const LLMSchemaIssueSchema = z
  .object({
    code: z.string().max(60),
    path: z.string().max(200)
  })
  .strict()
export type LLMSchemaIssue = z.infer<typeof LLMSchemaIssueSchema>

export const MAX_LLM_SCHEMA_ISSUES = 20

export const LLMFailureDiagnosticSchema = z
  .object({
    providerCode: ProposalSystemErrorCodeEnum,
    providerKind: NormalizedLLMErrorKindEnum,
    outputFailure: LLMOutputFailureEnum.nullable(),
    schemaIssues: z.array(LLMSchemaIssueSchema).max(MAX_LLM_SCHEMA_ISSUES)
  })
  .strict()
export type LLMFailureDiagnostic = z.infer<typeof LLMFailureDiagnosticSchema>
